import {
  ProvincialTaxVersionResponse,
  TaxTypeCanadaPensionPlan,
  TaxTypeCanadaPensionPlan2,
  TaxTypeQuebecPensionPlan,
  TaxTypeQuebecPensionPlan2,
  TaxTypeEmploymentInsurance,
  TaxTypeHealthCare
} from '../../hooks/use-tax-api';

/**
 * Calculate CPP employer cost using real API data
 * @param annualSalary - Annual salary amount
 * @param cppData - CPP data from API
 * @returns Employer CPP contribution amount
 */
export function calculateEmployerCPPCost(
  annualSalary: number, 
  cppData: TaxTypeCanadaPensionPlan
): number {
  if (!cppData?.IsEligible || annualSalary <= 0) {
    return 0;
  }

  // Step 1: Calculate pensionable earnings (subtract exemption)
  const pensionableEarnings = Math.max(0, annualSalary - cppData.AnnualExemption);
  
  // Step 2: Apply earnings cap (max pensionable minus exemption)
  const cappedEarnings = Math.min(
    pensionableEarnings, 
    cppData.MaxPensionableEarnings - cppData.AnnualExemption
  );
  
  // Step 3: Calculate employee CPP contribution
  const employeeCPP = cappedEarnings * (cppData.EmployeeRatePercentage / 100);
  
  // Step 4: Apply maximum contribution limit
  const finalEmployeeCPP = Math.min(employeeCPP, cppData.MaxDeductions);
  
  // Step 5: Calculate employer cost (employer matches employee when multiplier = 1.00)
  const employerCost = finalEmployeeCPP * cppData.EmployerMultiplerPercentage;
  
  return employerCost;
}

/**
 * Calculate CPP2 (Enhanced CPP) employer cost using real API data
 * @param annualSalary - Annual salary amount
 * @param cpp2Data - CPP2 data from API
 * @param cppData - CPP1 data (needed for threshold)
 * @returns Employer CPP2 contribution amount
 */
export function calculateEmployerCPP2Cost(
  annualSalary: number,
  cpp2Data: TaxTypeCanadaPensionPlan2,
  cppData: TaxTypeCanadaPensionPlan
): number {
  if (!cpp2Data?.IsEligible || !cppData?.IsEligible || annualSalary <= 0) {
    return 0;
  }

  const cpp1Threshold = cppData.MaxPensionableEarnings;
  
  // No CPP2 if salary is under the CPP1 threshold
  if (annualSalary <= cpp1Threshold) {
    return 0;
  }
  
  // Step 1: Calculate CPP2 pensionable earnings (above CPP1 threshold)
  const cpp2PensionableEarnings = Math.min(
    annualSalary - cpp1Threshold, 
    cpp2Data.MaxPensionableEarnings - cpp1Threshold
  );
  
  // Step 2: Calculate employee CPP2 contribution
  const employeeCPP2 = cpp2PensionableEarnings * (cpp2Data.EmployeeRatePercentage / 100);
  
  // Step 3: Apply CPP2 maximum contribution limit
  const finalEmployeeCPP2 = Math.min(employeeCPP2, cpp2Data.MaxDeductions);
  
  // Step 4: Employer matches employee contribution
  const employerCPP2Cost = finalEmployeeCPP2 * cpp2Data.EmployerMultiplerPercentage;
  
  return employerCPP2Cost;
}

/**
 * Calculate QPP employer cost using real API data
 * @param annualSalary - Annual salary amount
 * @param qppData - QPP data from API
 * @returns Employer QPP contribution amount
 */
export function calculateEmployerQPPCost(
  annualSalary: number, 
  qppData: TaxTypeQuebecPensionPlan
): number {
  if (!qppData?.IsEligible || annualSalary <= 0) {
    return 0;
  }

  // Step 1: Calculate pensionable earnings (subtract exemption)
  const pensionableEarnings = Math.max(0, annualSalary - qppData.AnnualExemption);
  
  // Step 2: Apply earnings cap
  const cappedEarnings = Math.min(
    pensionableEarnings, 
    qppData.MaxPensionableEarnings - qppData.AnnualExemption
  );
  
  // Step 3: Calculate employee QPP contribution
  const employeeQPP = cappedEarnings * (qppData.EmployeeRatePercentage / 100);
  
  // Step 4: Apply maximum contribution limit
  const finalEmployeeQPP = Math.min(employeeQPP, qppData.MaxDeductions);
  
  // Step 5: Calculate employer cost
  const employerCost = finalEmployeeQPP * qppData.EmployerMultiplerPercentage;
  
  return employerCost;
}

/**
 * Calculate QPP2 employer cost using real API data
 * @param annualSalary - Annual salary amount
 * @param qpp2Data - QPP2 data from API
 * @param qppData - QPP1 data (needed for threshold)
 * @returns Employer QPP2 contribution amount
 */
export function calculateEmployerQPP2Cost(
  annualSalary: number,
  qpp2Data: TaxTypeQuebecPensionPlan2,
  qppData: TaxTypeQuebecPensionPlan
): number {
  if (!qpp2Data?.IsEligible || !qppData?.IsEligible || annualSalary <= 0) {
    return 0;
  }

  const qpp1Threshold = qppData.MaxPensionableEarnings;
  
  // No QPP2 if salary is under the QPP1 threshold
  if (annualSalary <= qpp1Threshold) {
    return 0;
  }
  
  // Step 1: Calculate QPP2 pensionable earnings
  const qpp2PensionableEarnings = Math.min(
    annualSalary - qpp1Threshold, 
    qpp2Data.MaxPensionableEarnings - qpp1Threshold
  );
  
  // Step 2: Calculate employee QPP2 contribution
  const employeeQPP2 = qpp2PensionableEarnings * (qpp2Data.EmployeeRatePercentage / 100);
  
  // Step 3: Apply QPP2 maximum contribution limit
  const finalEmployeeQPP2 = Math.min(employeeQPP2, qpp2Data.MaxDeductions);
  
  // Step 4: Employer matches employee contribution
  const employerQPP2Cost = finalEmployeeQPP2 * qpp2Data.EmployerMultiplerPercentage;
  
  return employerQPP2Cost;
}

/**
 * Calculate Employment Insurance employer cost using real API data
 * @param annualSalary - Annual salary amount
 * @param eiData - EI data from API
 * @returns Employer EI contribution amount
 */
export function calculateEmployerEICost(
  annualSalary: number,
  eiData: TaxTypeEmploymentInsurance
): number {
  if (!eiData?.IsEligible || annualSalary <= 0) {
    return 0;
  }

  // Step 1: Apply maximum insurable earnings cap
  const cappedEarnings = Math.min(annualSalary, eiData.MaxInsurable);
  
  // Step 2: Calculate employee EI contribution
  const employeeEI = cappedEarnings * (eiData.EmployeeRatePercentage / 100);
  
  // Step 3: Apply maximum contribution limit (based on MaxEarnings field)
  const finalEmployeeEI = Math.min(employeeEI, eiData.MaxEarnings);
  
  // Step 4: Calculate employer cost (employer pays a multiple of employee contribution)
  const employerCost = finalEmployeeEI * eiData.EmployerMultiplerPercentage;
  
  return employerCost;
}

/**
 * Calculate Employer Health Tax (EHT) using real API data
 * @param annualSalary - Annual salary amount
 * @param ehtData - EHT data from API
 * @returns Employer EHT contribution amount
 */
export function calculateEmployerEHTCost(
  annualSalary: number,
  ehtData: TaxTypeHealthCare
): number {
  if (!ehtData?.IsEligible || annualSalary <= 0) {
    return 0;
  }

  // Apply the EHT rate to the gross earnings (simple percentage calculation)
  const ehtCost = annualSalary * (ehtData.EmployeeRatePercentage / 100);
  
  return ehtCost;
}

/**
 * Calculate total pension contribution (CPP + CPP2 for ROC, QPP + QPP2 for Quebec)
 * @param annualSalary - Annual salary amount
 * @param taxData - Provincial tax data from API
 * @returns Object with breakdown of pension contributions
 */
export function calculateTotalPensionContribution(
  annualSalary: number,
  taxData: ProvincialTaxVersionResponse
) {
  // Determine if this is Quebec (QPP) or Rest of Canada (CPP)
  const isQuebec = taxData.TaxTypeQuebecPensionPlan?.IsEligible || false;
  
  if (isQuebec) {
    const qpp1Cost = calculateEmployerQPPCost(annualSalary, taxData.TaxTypeQuebecPensionPlan);
    const qpp2Cost = calculateEmployerQPP2Cost(
      annualSalary, 
      taxData.TaxTypeQuebecPensionPlan2, 
      taxData.TaxTypeQuebecPensionPlan
    );
    
    return {
      pensionType: 'QPP',
      pension1: qpp1Cost,
      pension2: qpp2Cost,
      total: qpp1Cost + qpp2Cost
    };
  } else {
    const cpp1Cost = calculateEmployerCPPCost(annualSalary, taxData.TaxTypeCanadaPensionPlan);
    const cpp2Cost = calculateEmployerCPP2Cost(
      annualSalary, 
      taxData.TaxTypeCanadaPensionPlan2, 
      taxData.TaxTypeCanadaPensionPlan
    );
    
    return {
      pensionType: 'CPP',
      pension1: cpp1Cost,
      pension2: cpp2Cost,
      total: cpp1Cost + cpp2Cost
    };
  }
}

/**
 * Calculate all employment-related contributions using real API data
 * @param annualSalary - Annual salary amount
 * @param taxData - Provincial tax data from API
 * @returns Object with all contribution breakdowns
 */
export function calculateAllEmploymentContributions(
  annualSalary: number,
  taxData: ProvincialTaxVersionResponse
) {
  const pensionContributions = calculateTotalPensionContribution(annualSalary, taxData);
  const eiContribution = calculateEmployerEICost(annualSalary, taxData.TaxTypeEmploymentInsurance);
  const ehtContribution = calculateEmployerEHTCost(annualSalary, taxData.TaxTypeHealthCare);
  
  return {
    ...pensionContributions,
    employmentInsurance: eiContribution,
    employerHealthTax: ehtContribution,
    grandTotal: pensionContributions.total + eiContribution + ehtContribution
  };
} 